/**
 * Frontend Token Refresh Test Utility
 * This utility helps test and debug token refresh functionality in the browser
 */

// Utility to decode JWT tokens
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

// Utility to check if token is expired or will expire soon
function isTokenExpiredOrExpiringSoon(token, bufferMinutes = 5) {
  if (!token) return true;
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const bufferTime = bufferMinutes * 60 * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  
  return expirationTime <= (currentTime + bufferTime);
}

// Utility to get token expiration time
function getTokenExpirationTime(token) {
  if (!token) return null;
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return null;
  
  return new Date(decoded.exp * 1000);
}

// Utility to get time until token expires
function getTimeUntilExpiration(token) {
  if (!token) return null;
  
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return null;
  
  const currentTime = new Date();
  const timeDiff = expirationTime.getTime() - currentTime.getTime();
  
  return Math.max(0, timeDiff);
}

// Format time difference in human readable format
function formatTimeDifference(milliseconds) {
  if (milliseconds <= 0) return 'Expired';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

// Test token refresh functionality
export async function testTokenRefresh() {
  console.log('🧪 Testing Token Refresh Functionality');
  console.log('=' * 50);
  
  // Get current tokens
  const accessToken = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');
  const user = localStorage.getItem('user');
  
  console.log('Current State:');
  console.log(`- Access Token: ${accessToken ? 'Present' : 'Missing'}`);
  console.log(`- Refresh Token: ${refreshToken ? 'Present' : 'Missing'}`);
  console.log(`- User: ${user ? 'Present' : 'Missing'}`);
  
  if (!accessToken) {
    console.log('❌ No access token found. Please log in first.');
    return false;
  }
  
  // Decode and analyze tokens
  const accessTokenDecoded = decodeJWT(accessToken);
  const refreshTokenDecoded = refreshToken ? decodeJWT(refreshToken) : null;
  
  console.log('\nToken Analysis:');
  if (accessTokenDecoded) {
    const expirationTime = getTokenExpirationTime(accessToken);
    const timeUntilExpiration = getTimeUntilExpiration(accessToken);
    const isExpiringSoon = isTokenExpiredOrExpiringSoon(accessToken);
    
    console.log(`- Access Token Expiration: ${expirationTime}`);
    console.log(`- Time Until Expiration: ${formatTimeDifference(timeUntilExpiration)}`);
    console.log(`- Expiring Soon (< 5 min): ${isExpiringSoon ? 'Yes' : 'No'}`);
    console.log(`- User: ${accessTokenDecoded.sub || 'Unknown'}`);
  }
  
  if (refreshTokenDecoded) {
    const expirationTime = getTokenExpirationTime(refreshToken);
    const timeUntilExpiration = getTimeUntilExpiration(refreshToken);
    
    console.log(`- Refresh Token Expiration: ${expirationTime}`);
    console.log(`- Time Until Expiration: ${formatTimeDifference(timeUntilExpiration)}`);
  }
  
  // Test API call with current token
  console.log('\nTesting API Call:');
  try {
    const response = await fetch('/api/users/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const userData = await response.json();
      console.log(`✅ API call successful - User: ${userData.username}`);
    } else if (response.status === 401) {
      console.log('❌ API call failed - Unauthorized (token may be expired)');
    } else {
      console.log(`⚠️ API call failed - Status: ${response.status}`);
    }
  } catch (error) {
    console.log(`❌ API call error: ${error.message}`);
  }
  
  // Test manual token refresh
  if (refreshToken) {
    console.log('\nTesting Manual Token Refresh:');
    try {
      const response = await fetch('/api/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (response.ok) {
        const refreshData = await response.json();
        console.log('✅ Manual token refresh successful');
        console.log(`- New Access Token: ${refreshData.access_token ? 'Present' : 'Missing'}`);
        console.log(`- New Refresh Token: ${refreshData.refresh_token ? 'Present' : 'Missing'}`);
        
        // Update stored tokens
        if (refreshData.access_token) {
          localStorage.setItem('token', refreshData.access_token);
        }
        if (refreshData.refresh_token) {
          localStorage.setItem('refreshToken', refreshData.refresh_token);
        }
        
        return true;
      } else {
        console.log(`❌ Manual token refresh failed - Status: ${response.status}`);
        const errorData = await response.json().catch(() => ({}));
        console.log(`- Error: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.log(`❌ Manual token refresh error: ${error.message}`);
    }
  } else {
    console.log('⚠️ No refresh token available for testing');
  }
  
  return false;
}

// Monitor token refresh events
export function monitorTokenRefresh() {
  console.log('🔍 Monitoring Token Refresh Events');
  
  // Monitor localStorage changes
  const originalSetItem = localStorage.setItem;
  const originalRemoveItem = localStorage.removeItem;
  
  localStorage.setItem = function(key, value) {
    if (key === 'token') {
      console.log('🔄 Access token updated');
      const decoded = decodeJWT(value);
      if (decoded) {
        const expirationTime = getTokenExpirationTime(value);
        const timeUntilExpiration = getTimeUntilExpiration(value);
        console.log(`- New expiration: ${expirationTime}`);
        console.log(`- Time until expiration: ${formatTimeDifference(timeUntilExpiration)}`);
      }
    } else if (key === 'refreshToken') {
      console.log('🔄 Refresh token updated');
    }
    originalSetItem.apply(this, arguments);
  };
  
  localStorage.removeItem = function(key) {
    if (key === 'token') {
      console.log('🗑️ Access token removed');
    } else if (key === 'refreshToken') {
      console.log('🗑️ Refresh token removed');
    }
    originalRemoveItem.apply(this, arguments);
  };
  
  // Monitor session expired events
  window.addEventListener('session-expired', () => {
    console.log('⚠️ Session expired event triggered');
  });
  
  // Monitor network requests for 401 errors
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    return originalFetch.apply(this, args).then(response => {
      if (response.status === 401) {
        console.log('⚠️ 401 Unauthorized response detected');
        console.log(`- URL: ${args[0]}`);
      }
      return response;
    });
  };
  
  console.log('✅ Token refresh monitoring enabled');
}

// Utility to manually trigger token refresh
export async function manualTokenRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.log('❌ No refresh token available');
    return false;
  }
  
  try {
    const response = await fetch('/api/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });
    
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      console.log('✅ Manual token refresh successful');
      return true;
    } else {
      console.log(`❌ Manual token refresh failed: ${response.status}`);
      return false;
    }
  } catch (error) {
    console.log(`❌ Manual token refresh error: ${error.message}`);
    return false;
  }
}

// Export utilities for use in browser console
window.tokenRefreshTest = {
  testTokenRefresh,
  monitorTokenRefresh,
  manualTokenRefresh,
  decodeJWT,
  isTokenExpiredOrExpiringSoon,
  getTokenExpirationTime,
  getTimeUntilExpiration,
  formatTimeDifference
};

console.log('🔧 Token Refresh Test Utilities loaded');
console.log('Available functions:');
console.log('- window.tokenRefreshTest.testTokenRefresh()');
console.log('- window.tokenRefreshTest.monitorTokenRefresh()');
console.log('- window.tokenRefreshTest.manualTokenRefresh()');
console.log('- window.tokenRefreshTest.decodeJWT(token)');
console.log('- window.tokenRefreshTest.isTokenExpiredOrExpiringSoon(token)');
console.log('- window.tokenRefreshTest.getTokenExpirationTime(token)');
console.log('- window.tokenRefreshTest.getTimeUntilExpiration(token)');
console.log('- window.tokenRefreshTest.formatTimeDifference(milliseconds)');

// Test if utilities are working
console.log('🧪 Testing utility functions...');
const testToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0IiwiaWF0IjoxNjE2MjM5MDIyLCJleHAiOjk5OTk5OTk5OTl9.test';
console.log('Test token decoded:', window.tokenRefreshTest.decodeJWT(testToken));
console.log('✅ Token refresh utilities are ready!'); 